import { FocusEvent, ForwardedRef, forwardRef, memo, useCallback, useRef, useState } from 'react'

import { SxProps } from '@mui/material'
import clsx from 'clsx'

import { hasContentInComponent, randomString } from '@/utils'
import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import { Popper } from '@/components/Surface'

import { inputClasses } from './Input.data'
import { InputProps } from './Input.types'
import { inputStyles as styles } from './Input.style'

import InputBase from './InputBase'
import InputLabel from './InputLabel'
import { EndAdornment, StartAdornment } from './InputAdornments'

const Input = forwardRef(
  (
    {
      sx,
      inputProps = {},
      errorText = '',
      label = '',
      labelPlacement = 'inside',
      labelProps = {},
      active = false,
      startAdornment,
      endAdornment,
      onFocus,
      onBlur,
      inputRef: inputRefProp,
      DropDown,
      dropdownProps = {},
      containerProps = {},
      inputWrapperProps = {},
      startAdornmentProps = {},
      endAdornmentProps = {},
      errorTextProps = {},
      ...props
    }: InputProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [inputId] = useState(`input-${randomString(5)}`)
    const [hasFocus, setHasFocus] = useState(false)

    // refs management
    const containerRefInner = useRef<HTMLDivElement>(null)
    const containerRef = ref ?? containerRefInner

    const inputRefInner = useRef<HTMLInputElement>(null)
    const inputRef = inputRefProp ?? inputRefInner

    const [isDropDownOpen, setIsDropDownOpen] = useState(false)

    // Handlers
    const focusHandler = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setHasFocus(true)
        onFocus?.(event)
        setIsDropDownOpen(true)
      },
      [onFocus],
    )

    const blurHandler = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setHasFocus(false)
        onBlur?.(event)
        setIsDropDownOpen(false)
      },
      [onBlur],
    )

    const getAnchorElForDropdown = useCallback((reference: typeof containerRef) => {
      if (typeof reference === 'function') return null

      return reference && 'current' in reference ? reference.current : null
    }, [])

    // Props extraction
    const { sx: inputSx, ...otherInputProps } = inputProps

    const {
      sx: containerSx,
      className: containerClassName = '',
      onClick: onContainerClick,
      ...otherContainerProps
    } = containerProps

    const { sx: inputWrapperSx, ...otherInputWrapperProps } = inputWrapperProps
    const { sx: errorTextSx, ...otherErrorTextProps } = errorTextProps

    // utils
    const isActive = Boolean(active || hasFocus)
    const shrinkLabel = Boolean(labelPlacement === 'inside' && (isActive || props.value))

    const hasDropDown = hasContentInComponent(DropDown)

    return (
      <>
        <Box
          ref={containerRef}
          sx={[styles.container, containerSx] as SxProps}
          className={clsx({
            [inputClasses.state.error]: errorText || props.error,
            [inputClasses.state.disabled]: props.disabled,
            [inputClasses.state.focused]: isActive,
            [inputClasses.state.startAdornment]: !!startAdornment,
            [inputClasses.state.endAdornment]: !!endAdornment,
            [inputClasses.label.placement.inside]: label && labelPlacement === 'inside',
            [inputClasses.label.placement.outside]: labelPlacement === 'outside',
            [containerClassName]: Boolean(containerClassName),
          })}
          onClick={(event) => {
            inputRef.current?.focus()
            onContainerClick?.(event)
          }}
          {...otherContainerProps}>
          {label && (
            <InputLabel id={inputId} shrinkLabel={shrinkLabel} {...labelProps}>
              {label}
            </InputLabel>
          )}

          <Box sx={[styles.inputWrapper, inputWrapperSx] as SxProps} {...otherInputWrapperProps}>
            {startAdornment && (
              <StartAdornment shrinkLabel={shrinkLabel} {...startAdornmentProps}>
                {startAdornment}
              </StartAdornment>
            )}

            <InputBase
              aria-labelledby={inputId}
              sx={[styles.inputBase, sx] as SxProps}
              inputProps={{ sx: [styles.input, inputSx], ...otherInputProps }}
              error={!!errorText}
              onFocus={focusHandler}
              onBlur={blurHandler}
              inputRef={inputRef}
              {...props}
            />

            {endAdornment && <EndAdornment {...endAdornmentProps}>{endAdornment}</EndAdornment>}
          </Box>

          {errorText && (
            <Text sx={[styles.errorText, errorTextSx] as SxProps} {...otherErrorTextProps}>
              {errorText}
            </Text>
          )}
        </Box>

        {hasDropDown && (
          <Popper
            open={isDropDownOpen}
            anchorEl={getAnchorElForDropdown(containerRef)}
            parentWidth
            {...dropdownProps}>
            {DropDown}
          </Popper>
        )}
      </>
    )
  },
)

Input.displayName = 'Input'

export default memo(Input)
